import { useRef, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import SectionCard from "../../components/account/SectionCard";
import ShopSelect from "../../components/ShopSelect";
import useNotifications from "../../features/account/useNotifications";
import { timeAgo } from "../../utils/timeAgo";
import { useTimeRefresh } from "../../hooks/useTimeRefresh";
import { resolveAccountNotificationUrl } from "../../utils/notificationLinks";
import Swal from "../../utils/apparifySwal";
import { ListSkeleton } from "../../components/skeletons/ShopperSkeletons";
import {
  Bell,
  Package,
  ShieldAlert,
  Megaphone,
  Info,
  Check,
  CheckCheck,
  Loader2,
  Store,
  Filter,
  UserPlus,
  X,
} from "lucide-react";

const LEGACY_ACTION_ROUTE_MAP = {
  "/resubmit-application": "/business/resubmit",
};

function resolveNotificationActionUrl(template, metadata = {}) {
  if (!template || typeof template !== "string") return null;

  const interpolated = template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = metadata?.[key];
    return value == null ? "" : String(value);
  });

  for (const [legacyPrefix, currentPrefix] of Object.entries(LEGACY_ACTION_ROUTE_MAP)) {
    if (interpolated === legacyPrefix || interpolated.startsWith(`${legacyPrefix}/`)) {
      return interpolated.replace(legacyPrefix, currentPrefix);
    }
  }

  return interpolated;
}

/* ═══════════════════════════════════════════════
   Category → icon + colour mapping
   ═══════════════════════════════════════════════ */
const CAT_META = {
  ORDER:      { Icon: Package,     color: "text-blue-500",    bg: "bg-blue-50    dark:bg-blue-900/30" },
  SECURITY:   { Icon: ShieldAlert, color: "text-red-500",     bg: "bg-red-50     dark:bg-red-900/30" },
  ACCOUNT:    { Icon: Store,       color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-900/30" },
  PROMO:      { Icon: Megaphone,   color: "text-pink-500",    bg: "bg-pink-50    dark:bg-pink-900/30" },
  SYSTEM:     { Icon: Info,        color: "text-neutral-500", bg: "bg-neutral-100 dark:bg-neutral-800" },
  INVITATION: { Icon: UserPlus,    color: "text-violet-500",  bg: "bg-violet-50  dark:bg-violet-900/30" },
};

const CATEGORY_OPTIONS = [
  { value: "",           label: "All Categories" },
  { value: "ORDER",      label: "Orders" },
  { value: "SECURITY",   label: "Security" },
  { value: "ACCOUNT",    label: "Account" },
  { value: "PROMO",      label: "Promotions" },
  { value: "SYSTEM",     label: "System" },
  { value: "INVITATION", label: "Invitations" },
];

/* ═══════════════════════════════════════════════
   Dynamic action buttons (event-driven actions)
   Renders action buttons from the backend's action instances.
   Used as a fallback for notifications that don't have
   hardcoded action components above.
   ═══════════════════════════════════════════════ */
function DynamicActionButtons({ actions, notification, onExecute }) {
  const [loadingId, setLoadingId] = useState(null);
  const navigate = useNavigate();

  const extractPayloadKeys = (payloadTemplate) => {
    if (!payloadTemplate) return [];

    const keys = new Set();
    const visit = (node) => {
      if (typeof node === 'string') {
        const matches = node.matchAll(/\{\{\s*payload\.([a-zA-Z0-9_.]+)\s*\}\}/g);
        for (const m of matches) keys.add(m[1]);
        return;
      }
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (node && typeof node === 'object') {
        Object.values(node).forEach(visit);
      }
    };

    visit(payloadTemplate);
    return Array.from(keys);
  };

  if (!actions?.length) return null;

  const available = actions.filter(a => a.status === 'AVAILABLE');
  const completed = actions.find(a => a.status === 'COMPLETED');

  if (completed) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
        <Check size={13} /> {completed.label || 'Done'}
      </div>
    );
  }

  if (!available.length) {
    const expired = actions.find(a => a.status === 'EXPIRED');
    if (expired) {
      return (
        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
          <Info size={13} /> Expired
        </div>
      );
    }
    return null;
  }

  const handleClick = async (e, instance) => {
    e.stopPropagation();
    setLoadingId(instance.id);
    try {
      if (instance.confirmationRequired) {
        const confirm = await Swal.fire({
          title: instance.label || 'Confirm action',
          text: instance.confirmationMessage || 'Are you sure you want to continue?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Continue',
        });
        if (!confirm.isConfirmed) return;
      }

      const payloadKeys = extractPayloadKeys(instance.payloadTemplate);
      const payload = {};

      for (const key of payloadKeys) {
        const label = key
          .split('.')
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
          .join(' ');
        const prompt = await Swal.fire({
          title: instance.label || 'Action input',
          input: 'textarea',
          inputLabel: label,
          inputPlaceholder: `Enter ${label.toLowerCase()}...`,
          showCancelButton: true,
          confirmButtonText: 'Submit',
          inputValidator: (v) => (!v || !String(v).trim() ? `${label} is required.` : null),
        });

        if (!prompt.isConfirmed) return;
        payload[key] = String(prompt.value || '').trim();
      }

      const result = await onExecute(notification.id, instance.id, payload);
      const linkUrl = instance.actionType === "LINK"
        ? resolveNotificationActionUrl(instance.endpointTemplate, notification?.metadata)
        : null;
      const targetUrl = resolveAccountNotificationUrl(notification, result?.navigateTo || linkUrl);

      if (targetUrl) {
        navigate(targetUrl);
      }
    } catch {
      /* parent handles errors */
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {available.map((a, i) => {
        const isPrimary = i === 0;
        const isLoading = loadingId === a.id;
        const isDisabled = loadingId !== null;

        return (
          <button
            key={a.id}
            type="button"
            disabled={isDisabled}
            onClick={(e) => handleClick(e, a)}
            className={isPrimary
              ? `inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5
                 text-xs font-semibold text-white shadow-sm
                 hover:bg-emerald-700 active:bg-emerald-800
                 disabled:opacity-50 disabled:cursor-not-allowed transition`
              : `inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5
                 text-xs font-semibold transition
                 border-neutral-300 text-neutral-600 bg-white
                 hover:bg-neutral-50 hover:text-neutral-800
                 dark:border-neutral-600 dark:text-neutral-300 dark:bg-neutral-800
                 dark:hover:bg-neutral-700 dark:hover:text-neutral-100
                 disabled:opacity-50 disabled:cursor-not-allowed`
            }
          >
            {isLoading && <Loader2 size={12} className="animate-spin" />}
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════ */
export default function NotificationsPage() {
  const { isDark } = useTheme();
  const navigate   = useNavigate();
  useTimeRefresh(30_000);

  const {
    items, meta, loading, hasMore, unreadCount,
    filter, setFilter,
    category, setCategory,
    loadMore,
    handleMarkRead, handleMarkAllRead, handleDelete,
    handleExecuteAction,
  } = useNotifications();

  const [actionError, setActionError]   = useState(null); // transient toast

  const handleDynamicExecute = useCallback(async (notificationId, instanceId, payload) => {
    setActionError(null);
    try {
      return await handleExecuteAction(notificationId, instanceId, payload);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to execute action.';
      setActionError(msg);
      throw err;
    }
  }, [handleExecuteAction]);

  /* ── Auto-dismiss error toast after 4s ───── */
  useEffect(() => {
    if (!actionError) return;
    const t = setTimeout(() => setActionError(null), 4000);
    return () => clearTimeout(t);
  }, [actionError]);

  /* ── Infinite scroll via IntersectionObserver ── */
  const sentinelRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) loadMore(); },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  /* ── Toolbar ── */
  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={handleMarkAllRead}
        disabled={unreadCount === 0}
        className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5
                   text-[11px] font-semibold tracking-wider uppercase
                   border-neutral-200 text-neutral-600 hover:bg-neutral-100
                   dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800
                   disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <CheckCheck size={13} /> Mark all read
      </button>
    </div>
  );

  /* ── Filter bar (tabs + category dropdown) ── */
  const filterBar = (
    <div className="flex flex-wrap items-center gap-3 mb-3">
      {/* All / Unread tabs */}
      <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {["all", "unread"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={[
              "px-3 py-1.5 text-xs font-semibold capitalize outline-none transition focus-visible:ring-2 focus-visible:ring-neutral-400/70 dark:focus-visible:ring-neutral-500/70",
              filter === f
                ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900"
                : "bg-white text-neutral-600 hover:bg-neutral-50 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800",
            ].join(" ")}
          >
            {f === "unread" ? `Unread (${unreadCount})` : "All"}
          </button>
        ))}
      </div>

      {/* Category dropdown */}
      <div className="flex items-center gap-1.5">
        <Filter size={13} className="text-neutral-500 dark:text-neutral-400 shrink-0" />
        <ShopSelect
          value={category}
          onValueChange={setCategory}
          options={CATEGORY_OPTIONS}
          placeholder="All Categories"
          triggerClassName="w-auto min-w-[10rem] rounded-lg px-2 py-1 text-xs"
          ariaLabel="Filter notifications by category"
        />
      </div>
    </div>
  );

  /* ── Empty state ── */
  const emptyState = (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-neutral-100 dark:bg-neutral-800 p-4 mb-4">
        <Bell size={28} className="text-neutral-400" />
      </div>
      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {filter === "unread" ? "No unread notifications" : "No notifications yet"}
      </p>
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
        {filter === "unread"
          ? "You're all caught up!"
          : "When you place orders or receive updates, they'll appear here."}
      </p>
    </div>
  );

  /* ── Loading skeleton ── */
  const skeleton = (
    <ListSkeleton rows={4} label="Loading notifications" />
  );

  /* ── Notification row ── */
  const renderItem = useCallback((n) => {
    const catMeta = CAT_META[n.category] || CAT_META.SYSTEM;
    const { Icon, color, bg } = catMeta;
    const targetUrl = resolveAccountNotificationUrl(n);

    return (
      <div
        key={n.id}
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!n.isRead) handleMarkRead(n.id);
          if (targetUrl) navigate(targetUrl);
        }}
        onKeyDown={(e) => e.key === 'Enter' && targetUrl && navigate(targetUrl)}
        className={[
          "group relative flex gap-3 rounded-xl p-3 transition cursor-pointer",
          n.isRead
            ? "bg-transparent hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
            : "bg-neutral-50 dark:bg-neutral-800/60 hover:bg-neutral-100 dark:hover:bg-neutral-800",
        ].join(" ")}
      >
        {/* Delete icon – top-right corner */}
        <button
          type="button"
          title="Delete notification"
          onClick={(e) => { e.stopPropagation(); handleDelete(n.id); }}
          className="absolute top-2 right-2 rounded-md p-1
                     opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100
                     text-neutral-400 hover:text-red-500 hover:bg-red-50
                     dark:hover:text-red-400 dark:hover:bg-red-950/30
                     transition-all"
        >
          <X size={13} />
        </button>

        {/* Icon */}
        <div className={`shrink-0 flex items-center justify-center h-9 w-9 rounded-full ${bg}`}>
          <Icon size={16} className={color} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={[
                "text-sm font-medium leading-snug truncate",
                n.isRead
                  ? "text-neutral-600 dark:text-neutral-400"
                  : "text-neutral-900 dark:text-neutral-50",
              ].join(" ")}>
                {n.title}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2">
                {n.body}
              </p>
            </div>

            <div className="shrink-0 flex flex-col items-end gap-1">
              <span className="text-[10px] text-neutral-400 dark:text-neutral-500 whitespace-nowrap">
                {timeAgo(n.createdAt)}
              </span>
              {!n.isRead && (
                <span className="h-2 w-2 rounded-full bg-blue-500" title="Unread" />
              )}
            </div>
          </div>

          {/* ── Dynamic action buttons (single data-driven rendering path) ── */}
          {n.actions?.length > 0 && (
            <DynamicActionButtons
              actions={n.actions}
              notification={n}
              onExecute={handleDynamicExecute}
            />
          )}

          {/* Mark as read action */}
          {!n.isRead && (
            <div className="mt-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                className="inline-flex items-center gap-1 text-[10px] font-medium tracking-wider uppercase
                           text-neutral-500 hover:text-neutral-800
                           dark:text-neutral-400 dark:hover:text-neutral-200 transition"
              >
                <Check size={11} /> Mark as read
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }, [handleMarkRead, handleDelete, handleDynamicExecute, navigate]);

  return (
    <SectionCard
      title="Notifications"
      subtitle={meta?.total != null ? `${meta.total} notification${meta.total !== 1 ? "s" : ""}` : undefined}
      isDark={isDark}
      action={toolbar}
    >
      {/* Error toast */}
      {actionError && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50
                        px-3 py-2 text-xs text-red-700
                        dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-300">
          <ShieldAlert size={14} className="shrink-0" />
          <span className="flex-1">{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} className="p-0.5 hover:opacity-70">
            <X size={12} />
          </button>
        </div>
      )}

      {filterBar}

      {loading && items.length === 0
        ? skeleton
        : items.length === 0
          ? emptyState
          : (
            <div className="space-y-1">
              {items.map(renderItem)}

              {/* Sentinel for infinite scroll */}
              <div ref={sentinelRef} className="h-1" />

              {loading && (
                <div className="flex justify-center py-4">
                  <Loader2 size={20} className="animate-spin text-neutral-400" />
                </div>
              )}

              {!hasMore && items.length > 0 && (
                <p className="text-center text-[11px] text-neutral-400 dark:text-neutral-500 py-3">
                  You've reached the end
                </p>
              )}
            </div>
          )
      }
    </SectionCard>
  );
}
